import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, Loader2, X, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API } from './adminConstants';

const AdminRetentionModal = ({ onClose, onChanged }) => {
  const { getToken } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('would');

  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/retention`, { headers: headers() });
      setData(res.data);
    } catch { toast.error('Erro ao carregar a lista de retenção'); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async (patch) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/retention/settings`, patch, { headers: headers() });
      setData(prev => ({ ...prev, settings: res.data }));
      toast.success('Configuração salva');
      onChanged?.();
    } catch { toast.error('Erro ao salvar configuração'); }
    finally { setSaving(false); }
  };

  const toggleExempt = async (company) => {
    try {
      await axios.put(`${API}/admin/company/${company.id}/block-exempt`,
        { block_exempt: !company.block_exempt }, { headers: headers() });
      setData(prev => ({
        ...prev,
        companies: prev.companies.map(c => c.id === company.id
          ? { ...c, block_exempt: !company.block_exempt, exempt: !company.block_exempt ? true : c.exempt }
          : c),
      }));
      onChanged?.();
    } catch { toast.error('Erro ao alterar a empresa'); }
  };

  const rows = (data?.companies || []).filter(c => {
    if (filter === 'would') return c.would_retain;
    if (filter === 'exempt') return c.block_exempt;
    return true;
  });

  const settings = data?.settings || {};

  return (
    <div className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-4" data-testid="retention-modal">
      <Card className="w-full max-w-4xl max-h-[88vh] overflow-hidden flex flex-col">
        <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-600" />
              Avaliação Retida
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl">
              Quando o teste de 30 dias termina sem pagamento ou o boleto passa de {settings.overdue_grace_days ?? 20} dias vencido, a avaliação
              continua sendo gravada e o cliente final não vê nada diferente — mas o conteúdo fica oculto para o
              dono até ele regularizar. Marque abaixo quem deve ficar de fora.
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600" data-testid="close-retention-modal"><X className="w-5 h-5" /></button>
        </CardHeader>

        <div className="px-6 pb-3 flex flex-wrap items-center gap-3 border-b">
          <button
            onClick={() => saveSettings({ enabled: !settings.enabled })}
            disabled={saving}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold ${settings.enabled ? 'bg-amber-600 text-white hover:bg-amber-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
            data-testid="retention-enabled-toggle"
          >
            Modo retido: {settings.enabled ? 'LIGADO' : 'desligado'}
          </button>
          <button
            onClick={() => saveSettings({ reminders_enabled: !settings.reminders_enabled })}
            disabled={saving || !settings.enabled}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-50 ${settings.reminders_enabled ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
            data-testid="retention-reminders-toggle"
          >
            Lembretes 10h (WhatsApp + e-mail): {settings.reminders_enabled ? 'LIGADOS' : 'desligados'}
          </button>
          <span className="flex items-center gap-2 text-xs text-slate-600">
            Reter após
            <input
              type="number"
              min={1}
              max={90}
              defaultValue={settings.overdue_grace_days ?? 20}
              onBlur={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!v || v < 1 || v > 90) {
                  toast.error('Informe de 1 a 90 dias');
                  e.target.value = settings.overdue_grace_days ?? 20;
                  return;
                }
                if (v !== settings.overdue_grace_days) saveSettings({ overdue_grace_days: v });
              }}
              className="w-16 h-8 px-2 border border-slate-300 rounded-lg text-center font-bold"
              data-testid="retention-grace-days"
            />
            dias de boleto vencido
          </span>
          <span className="text-xs text-slate-500">
            {data?.would_retain_count || 0} empresa(s) entrariam em retenção hoje
          </span>
        </div>

        <div className="px-6 py-3 flex gap-2 text-xs">
          {[['would', 'Entrariam em retenção'], ['exempt', 'Isentas'], ['all', 'Todas']].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`px-3 py-1.5 rounded-lg font-semibold ${filter === k ? 'bg-[#0A2647] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`retention-filter-${k}`}
            >
              {label}
            </button>
          ))}
        </div>

        <CardContent className="overflow-y-auto flex-1">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">Nenhuma empresa nesta lista.</p>
          ) : (
            <div className="space-y-2">
              {rows.map(c => (
                <div key={c.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200" data-testid={`retention-row-${c.id}`}>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-slate-900 truncate">
                      {c.name}
                      <span className="ml-2 text-xs font-normal text-slate-400">{c.plan_name} · {c.subscription_status}</span>
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {c.would_retain && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">{c.pending_count > 0 ? `${c.pending_count} retida(s)` : 'entraria em retenção'}</span>}
                      {c.manual && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">retida manualmente</span>}
                      {c.is_test && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">TESTE</span>}
                      {c.billing_exempt && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-600">CORTESIA</span>}
                      {!c.has_document && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">SEM CNPJ</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => toggleExempt(c)}
                    className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold ${c.block_exempt ? 'bg-sky-100 text-sky-700 hover:bg-sky-200' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                    data-testid={`retention-exempt-${c.id}`}
                  >
                    <ShieldCheck className="w-3.5 h-3.5" />
                    {c.block_exempt ? 'Isenta' : 'Isentar'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>

        <div className="border-t p-4 flex items-center justify-between">
          <p className="text-xs text-slate-500">{rows.length} empresa(s) na lista · total {data?.total || 0}</p>
          <Button variant="outline" onClick={onClose} data-testid="retention-close-btn">Fechar</Button>
        </div>
      </Card>
    </div>
  );
};

export default AdminRetentionModal;
